import { Status, Badge } from '@nexelem/components';
import { ProLightIndustryWindows } from '@nexelem/icons/ProLightIndustryWindows';
import { useFormatTaskDuration } from '../../Gantt/hooks/useFormatTaskDuration';
import { useCustomTranslation } from '../../../../../hooks';
import type { ResourceDetailsView, ShortObjectView, BatchShortViewBase, TaskShortViewBase } from '@nexelem/graphne-api';
import { TaskActions } from './TaskActions';
import { CUSTOM_ICONS } from '@nexelem/icons';

type TasksProps = {
  tasks?: ShortObjectView[];
  workerData: ResourceDetailsView;
  isDrawer?: boolean;
};

export const Tasks = ({ tasks, workerData, isDrawer }: TasksProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');

  if (!tasks?.length) {
    return <div className="mt-2 text-s18">{tWrapper('noAssignedTasks')}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {tasks.map((task) =>
        task.tpe === 'Task' ? (
          <SingleTask isDrawer={isDrawer} key={task.id} task={task} workerData={workerData} />
        ) : (
          <BatchTask isDrawer={isDrawer} key={task.batchId} task={task} workerData={workerData} />
        ),
      )}
    </div>
  );
};

type SingleTaskProps = {
  task: TaskShortViewBase;
  isDrawer?: boolean;
  workerData: ResourceDetailsView;
};

const SingleTask = ({ task, isDrawer, workerData }: SingleTaskProps) => {
  const formatDuration = useFormatTaskDuration({ compact: true });

  const { queue, progress, start, end, productDetails } = task;
  const { amount, unit } = progress.totalAmount;
  const durationString = start && end && formatDuration({ startDate: new Date(start), endDate: new Date(end) });

  return (
    <div className="flex items-center gap-2 rounded-xs border border-border-container bg-surface-container px-2 py-3">
      <Status color="green" Icon={ProLightIndustryWindows} variant="icon" />
      <div className="flex flex-col gap-1">
        <div className="text-s11-sb">{productDetails.name}</div>
        <div className="text-s11-sb text-text-secondary-darker">
          <span className="mr-2">
            {amount} {unit.name}
          </span>
          <span>{durationString}</span>
        </div>
        <div className="text-s11-sb text-add-periwinkle70 uppercase">{queue?.name}</div>
      </div>

      {isDrawer && <TaskActions task={task} workerData={workerData} />}
    </div>
  );
};

type BatchTaskProps = {
  task: BatchShortViewBase;
  isDrawer?: boolean;
  workerData: ResourceDetailsView;
};

const BatchTask = ({ task, isDrawer, workerData }: BatchTaskProps) => {
  const { tasks, queue, start, end } = task;
  const formatDuration = useFormatTaskDuration({ compact: true });

  const durationString = start && end && formatDuration({ startDate: new Date(start), endDate: new Date(end) });

  return (
    <div className="flex items-center gap-2 rounded-xs border border-border-container bg-surface-container px-2 py-3">
      <Status color="green" Icon={ProLightIndustryWindows} variant="icon" />
      <Badge StartIcon={<CUSTOM_ICONS.Merge />}>{tasks?.length}</Badge>
      <div className="flex grow flex-col gap-1 text-s11-sb">
        {tasks?.map(({ name, id, progress }) => (
          <div className="flex w-full justify-between" key={id}>
            <span>{name}</span>
            <span className="text-text-secondary-darker">
              {progress.totalAmount.amount} {progress.totalAmount.unit.name}
            </span>
          </div>
        ))}
        <div className="flex w-full justify-between">
          <span className="text-add-periwinkle70">{queue?.name}</span>
          <span className="text-text-secondary-darker">{durationString}</span>
        </div>
      </div>

      {isDrawer && <TaskActions task={task} workerData={workerData} />}
    </div>
  );
};
